import * as THREE from 'three';

import {
  AdditiveBlending,
  BufferGeometry,
  Float32BufferAttribute,
  Group,
  Points,
  PointsMaterial,
} from 'three';
import React, { useMemo, useRef } from 'react';

import { useFrame } from '@react-three/fiber';

interface NavigationMarkerProps {
  position: [number, number, number];
  scale?: number;
}

export const NavigationMarker: React.FC<NavigationMarkerProps> = ({
  position,
  scale = 1,
}) => {
  // Use refs for animation
  const markerRef = useRef<Group>(null!);
  const glowRef = useRef<Group>(null!);
  const particlesRef = useRef<Points>(null!);
  const lightBeamRef = useRef<Group>(null!);
  const groundEffectRef = useRef<Group>(null!);
  const beamParticlesRef = useRef<Points>(null!);

  // Create particles around the marker
  const particles = useMemo(() => {
    const particleCount = 30; // Increased particle count
    const positions = [];
    const sizes = [];
    const colors = [];

    // Create random particle positions in a sphere around the marker
    for (let i = 0; i < particleCount; i++) {
      // Random position in sphere
      const radius = 2.0 * scale; // Larger radius
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);

      const x = radius * Math.sin(phi) * Math.cos(theta);
      const y = radius * Math.sin(phi) * Math.sin(theta);
      const z = radius * Math.cos(phi);

      positions.push(x, y, z);

      // Random size
      sizes.push(Math.random() * 0.08 * scale + 0.04 * scale); // Larger particles

      // Gold color with slight variations
      colors.push(1, 0.84 + Math.random() * 0.16, 0.3); // Slightly more orange gold
    }

    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
    geometry.setAttribute('size', new Float32BufferAttribute(sizes, 1));
    geometry.setAttribute('color', new Float32BufferAttribute(colors, 3));

    const material = new PointsMaterial({
      size: 0.15,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.7,
      vertexColors: true,
      blending: AdditiveBlending,
    });

    return { geometry, material };
  }, [scale]);

  // Create particles for the light beam
  const beamParticles = useMemo(() => {
    const particleCount = 50;
    const positions = [];
    const sizes = [];
    const colors = [];

    // Create random particles inside the light beam
    for (let i = 0; i < particleCount; i++) {
      // Random position within cylinder/cone shape
      const height = Math.random() * 25; // Random height within beam
      const radiusAtHeight = 0.1 + (height / 25) * 0.2; // Thinner beam, wider at bottom
      const theta = Math.random() * Math.PI * 2;
      const r = Math.sqrt(Math.random()) * radiusAtHeight * scale; // Sqrt for more even distribution

      const x = r * Math.cos(theta);
      const y = position[1] + 5 + height; // Start 5 units above marker
      const z = r * Math.sin(theta);

      positions.push(x, y, z);

      // Random size - smaller for dust effect
      sizes.push(Math.random() * 0.06 * scale + 0.02 * scale);

      // Light color with slight variations
      const brightness = 0.8 + Math.random() * 0.2; // Slightly varying brightness
      colors.push(brightness, brightness * 0.95, brightness * 0.8); // Warm white/yellow
    }

    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
    geometry.setAttribute('size', new Float32BufferAttribute(sizes, 1));
    geometry.setAttribute('color', new Float32BufferAttribute(colors, 3));

    const material = new PointsMaterial({
      size: 0.1,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.6,
      vertexColors: true,
      blending: AdditiveBlending,
    });

    return { geometry, material };
  }, [position, scale]);

  // Animate the marker to float up and down
  useFrame((state) => {
    if (markerRef.current) {
      markerRef.current.position.y =
        position[1] + Math.sin(state.clock.elapsedTime * 2) * 0.2 + 0.5;
      markerRef.current.rotation.y += 0.02;
    }

    // Animate the glow effect
    if (glowRef.current) {
      glowRef.current.scale.x = 1 + Math.sin(state.clock.elapsedTime * 3) * 0.1;
      glowRef.current.scale.y = 1 + Math.sin(state.clock.elapsedTime * 3) * 0.1;
      glowRef.current.scale.z = 1 + Math.sin(state.clock.elapsedTime * 3) * 0.1;
      glowRef.current.position.y =
        position[1] + Math.sin(state.clock.elapsedTime * 2) * 0.2 + 0.5;
    }

    // Animate particles
    if (particlesRef.current) {
      particlesRef.current.rotation.y += 0.01;
      particlesRef.current.position.y =
        position[1] + Math.sin(state.clock.elapsedTime * 2) * 0.2 + 0.5;

      // Pulse the particles
      const positions = particlesRef.current.geometry.attributes.position.array;
      const count = positions.length / 3;

      for (let i = 0; i < count; i++) {
        const i3 = i * 3;
        const x = positions[i3];
        const y = positions[i3 + 1];
        const z = positions[i3 + 2];

        const length = Math.sqrt(x * x + y * y + z * z);
        const factor = 1 + Math.sin(state.clock.elapsedTime * 2 + i) * 0.1;

        positions[i3] = (x / length) * factor * 2.0 * scale;
        positions[i3 + 1] = (y / length) * factor * 2.0 * scale;
        positions[i3 + 2] = (z / length) * factor * 2.0 * scale;
      }

      particlesRef.current.geometry.attributes.position.needsUpdate = true;
    }

    // Animate the light beam
    if (lightBeamRef.current) {
      // The parent group is already positioned at the marker's X,Z coordinates
      // We don't need to update its position unless the marker moves

      // Apply a subtle rotation only to the cylinder mesh, not the entire group
      const beamMesh = lightBeamRef.current.children[0] as THREE.Mesh;
      if (beamMesh) {
        beamMesh.rotation.y += 0.005;

        if (beamMesh.material) {
          const material = beamMesh.material as THREE.MeshBasicMaterial;
          material.opacity =
            0.1 + Math.sin(state.clock.elapsedTime * 1.5) * 0.15;
        }
      }
    }

    // Animate the beam particles
    if (beamParticlesRef.current) {
      // Slowly rotate the particles
      beamParticlesRef.current.rotation.y += 0.002;

      // Animate the dust particles floating upward
      const positions =
        beamParticlesRef.current.geometry.attributes.position.array;
      const count = positions.length / 3;

      for (let i = 0; i < count; i++) {
        const i3 = i * 3;

        // Move particles slowly upward
        positions[i3 + 1] += 0.02;

        // If a particle goes too high, reset it to the bottom
        if (positions[i3 + 1] > position[1] + 30) {
          const radiusAtBottom = 1.5 * scale;
          const theta = Math.random() * Math.PI * 2;
          const r = Math.sqrt(Math.random()) * radiusAtBottom;

          positions[i3] = r * Math.cos(theta);
          positions[i3 + 1] = position[1] + 5; // Reset to bottom of beam
          positions[i3 + 2] = r * Math.sin(theta);
        }

        // Add slight drift
        positions[i3] += (Math.random() - 0.5) * 0.01;
        positions[i3 + 2] += (Math.random() - 0.5) * 0.01;
      }

      beamParticlesRef.current.geometry.attributes.position.needsUpdate = true;
    }

    // Animate the ground effect
    if (groundEffectRef.current) {
      // Pulse the ground effect
      const pulse = 1 + Math.sin(state.clock.elapsedTime * 2) * 0.2;
      groundEffectRef.current.scale.x = pulse * scale;
      groundEffectRef.current.scale.z = pulse * scale;

      // Rotate the ground effect rings
      groundEffectRef.current.rotation.y += 0.02;
    }
  });

  return (
    <>
      {/* Light beam shining down from above */}
      <group ref={lightBeamRef} position={[position[0], 0, position[2]]}>
        {/* Light beam cylinder */}
        <mesh position={[0, position[1] + 15, 0]} scale={[scale, 130, scale]}>
          <cylinderGeometry args={[0.25, 0.75, 1, 16, 1, true]} />
          <meshBasicMaterial
            color="#FFE082"
            transparent={true}
            opacity={0.1}
            blending={AdditiveBlending}
            side={2} // Both sides
          />
        </mesh>

        {/* Dust particles floating in the beam */}
        <points
          ref={beamParticlesRef}
          geometry={beamParticles.geometry}
          material={beamParticles.material}
        />
      </group>

      {/* Ground effect with pulsing rings */}
      <group
        ref={groundEffectRef}
        position={[position[0], position[1] + 0.05, position[2]]}
      >
        {/* Main glow on the ground */}
        <mesh rotation={[Math.PI / 2, 0, 0]} scale={scale * 3}>
          <circleGeometry args={[1, 32]} />
          <meshBasicMaterial
            color="#FFE082"
            transparent={true}
            opacity={0.5}
            blending={AdditiveBlending}
          />
        </mesh>

        {/* Inner ring */}
        <mesh rotation={[Math.PI / 2, 0, 0]} scale={scale * 2.5}>
          <ringGeometry args={[0.8, 0.9, 32]} />
          <meshBasicMaterial
            color="#FFD54F"
            transparent={true}
            opacity={0.7}
            blending={AdditiveBlending}
          />
        </mesh>

        {/* Outer ring */}
        <mesh rotation={[Math.PI / 2, 0, 0]} scale={scale * 3.5}>
          <ringGeometry args={[0.95, 1, 32]} />
          <meshBasicMaterial
            color="#FFCA28"
            transparent={true}
            opacity={0.5}
            blending={AdditiveBlending}
          />
        </mesh>
      </group>

      {/* Glowing circle around the marker */}
      <group
        ref={glowRef}
        position={[position[0], position[1] + 0.5, position[2]]}
      >
        <mesh
          position={[0, 0, 0]}
          rotation={[Math.PI / 2, 0, 0]}
          scale={scale * 1.8}
        >
          <ringGeometry args={[0.5, 0.6, 32]} />
          <meshBasicMaterial
            color="#FFD54F"
            transparent={true}
            opacity={0.6}
            blending={AdditiveBlending}
          />
        </mesh>
      </group>

      {/* Particles */}
      <points
        ref={particlesRef}
        position={[position[0], position[1] + 0.5, position[2]]}
        geometry={particles.geometry}
        material={particles.material}
      />

      <group
        ref={markerRef}
        position={[position[0], position[1] + 0.5, position[2]]}
      >
        {/* Arrow pointing down */}
        <mesh position={[0, 0, 0]} rotation={[Math.PI, 0, 0]} scale={scale}>
          <coneGeometry args={[0.3, 0.6, 4]} />
          <meshLambertMaterial color="#FF9800" flatShading />
        </mesh>

        {/* Diamond shape on top */}
        <mesh
          position={[0, 0.5 * scale, 0]}
          rotation={[0, Math.PI / 4, 0]}
          scale={scale}
        >
          <octahedronGeometry args={[0.2, 0]} />
          <meshLambertMaterial color="#FFC107" flatShading />
        </mesh>
      </group>
    </>
  );
};
